import { useState, useEffect } from 'react'
import { X, Download, FileText, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog"
import { documentsAPI } from '@/utils/api'

/**
 * Modal component for PDF preview and generation.
 * Fetches PDF from backend and displays inline using iframe.
 */
export default function PDFPreviewModal({
  isOpen,
  onClose,
  documentId,
  documentTitle,
}) {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)

  // Fetch PDF preview when modal opens
  useEffect(() => {
    if (isOpen && documentId) {
      fetchPdfPreview()
    } else {
      // Clean up blob URL when modal closes
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
        setPdfUrl(null)
      }
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [isOpen, documentId])

  const fetchPdfPreview = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use centralized API with auth token
      const blob = await documentsAPI.previewPdf(documentId)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
    } catch (err) {
      console.error('Error fetching PDF preview:', err)
      setError(err.response?.data?.message || err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleGeneratePdf = async () => {
    setGenerating(true)
    setError(null)

    try {
      // Use centralized API with auth token
      const data = await documentsAPI.generatePdf(documentId)
      if (data.status === 'success') {
        // Refresh preview to show saved version
        await fetchPdfPreview()
      } else {
        throw new Error(data.message || 'Failed to generate PDF')
      }
    } catch (err) {
      console.error('Error generating PDF:', err)
      setError(err.response?.data?.message || err.message)
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!pdfUrl) return

    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = `${documentTitle || 'document'}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      {/* Hide the default close button that overlaps with our action buttons */}
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 [&>button:last-child]:hidden">
        <DialogHeader className="p-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {documentTitle || 'PDF Preview'}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPdfPreview}
                disabled={loading}
                className="gap-1"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={!pdfUrl || loading}
                className="gap-1"
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
              <Button
                size="sm"
                onClick={handleGeneratePdf}
                disabled={generating || loading}
                className="gap-1 bg-gradient-to-r from-action-from to-action-to hover:from-action-hover-from hover:to-action-hover-to text-action-text"
              >
                {generating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                {generating ? 'Saving...' : 'Save PDF'}
              </Button>
              {/* Custom close button in the header flex layout */}
              <DialogClose asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </DialogClose>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-sidebar-active mx-auto mb-4" />
                <p className="text-slate-600">Generating preview...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-medium mb-2">Failed to generate PDF</p>
                <p className="text-slate-500 text-sm mb-4">{error}</p>
                <Button onClick={fetchPdfPreview} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {pdfUrl && !loading && !error && (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="PDF Preview"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
